import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { SnButton } from "@shared/components/SnButton";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { showDialog } from "@widgets/CommonDialog";
import { showIssueModal } from "@widgets/IssueModal";
import SN_SFA_ISSUE_REG_005_POP_006 from "./SN_SFA_ISSUE_REG_005_POP_006";

interface popupProps {
  formData: any;
  modalRef?: any;
  Menuprops?: any;
  scriptList?: any[];
}

const POPUP_ID = "SN_SFA_ISSUE_REG_005_POP_003";
const CLIENT_TARGET_QUERY_ID = "sn_sfa_issue_reg_005_pop_006";

const trim = (value: any) => String(value ?? "").trim();

const selectedTargetCount = (rows?: any[]) =>
  Array.isArray(rows)
    ? rows.filter((row) => trim(row?.yn_selected) !== "N").length
    : 0;

const normalizeColumns = (columns: any[] = []) =>
  columns.map((column) => {
    if (column?.cd_column !== "object_type") return column;
    return {
      ...column,
      dc_filter:
        "String(orgData.dc_ref ?? '').trim() === String('{sync_type}').trim()",
    };
  });

const normalizeItemRow = (row: any, noIssue: string, cdUser: string) => ({
  ...row,
  no_issue: noIssue,
  seq: Number(row?.seq ?? 0) || null,
  sync_type: trim(row?.sync_type),
  object_type: trim(row?.object_type),
  source_service: trim(row?.source_service) || "snotes",
  object_ref: trim(row?.object_ref),
  yn_active: trim(row?.yn_active) || "Y",
  cd_created_by: trim(row?.cd_created_by) || cdUser,
  cd_updated_by: cdUser,
});

const SN_SFA_ISSUE_REG_005_POP_003 = forwardRef(
  (
    { formData, modalRef }: popupProps,
    ref: React.Ref<any>
  ) => {
    const { userInfo } = global();
    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
    });
    const noIssue = trim(formData?.no_issue);

    const [columns, setColumns] = useState<any>({ grid1: [] });
    const [dataSources, setDataSources] = useState<any>({ grid1: [] });
    const [popupParent, setPopupParent] = useState<any>(null);
    const [selectedClientTargets, setSelectedClientTargets] = useState<
      any[] | undefined
    >(undefined);
    const [persistedClientTargetCount, setPersistedClientTargetCount] =
      useState<number | undefined>(undefined);

    const refreshPersistedClientTargetCount = useCallback(async () => {
      if (!noIssue) {
        setPersistedClientTargetCount(undefined);
        return;
      }

      try {
        const data: any = await fnGetDataSet({
          id: CLIENT_TARGET_QUERY_ID,
          params: { no_issue: noIssue },
        });
        const rows = Array.isArray(data?.[0]) ? data[0] : [];
        setPersistedClientTargetCount(selectedTargetCount(rows));
      } catch {
        setPersistedClientTargetCount(undefined);
      }
    }, [noIssue]);

    const onSearch = useCallback(async () => {
      if (!noIssue) {
        setDataSources((prev: any) => ({ ...prev, grid1: [] }));
        setPersistedClientTargetCount(undefined);
        return;
      }

      try {
        const data: any = await fnGetDataSet({
          id: "sn_sfa_issue_reg_005_pop_003",
          params: { no_issue: noIssue },
        });
        setDataSources({
          grid1: Array.isArray(data?.[0]) ? data[0] : [],
        });
      } catch {
        setDataSources((prev: any) => ({ ...prev, grid1: [] }));
      }
      await refreshPersistedClientTargetCount();
    }, [noIssue, refreshPersistedClientTargetCount]);

    useEffect(() => {
      setPopupParent(modalRef?.current?.getPopupParent?.() ?? null);
    }, [modalRef]);

    useEffect(() => {
      setSelectedClientTargets(undefined);
      setPersistedClientTargetCount(undefined);
    }, [noIssue]);

    useEffect(() => {
      const fetchInit = async () => {
        try {
          const rtn: any = await getInitPopUpData(POPUP_ID);
          setColumns((prev: any) => ({
            ...prev,
            grid1: normalizeColumns(rtn?.grid?.grid1 || []),
          }));
          await onSearch();
        } catch {
          setColumns((prev: any) => ({ ...prev, grid1: [] }));
          setDataSources((prev: any) => ({ ...prev, grid1: [] }));
        }
      };
      fetchInit();
    }, [onSearch]);

    const getCurrentRows = () => {
      const rows: any[] = [];
      const grid = gridRefs.current["grid1"].current;
      grid?.stopEditing?.();
      grid?.forEachNode?.((node: any) => {
        if (node?.data) rows.push(node.data);
      });
      return rows;
    };

    const nextSeq = () => {
      const seqs = getCurrentRows().map((row) => Number(row?.seq ?? 0) || 0);
      return (seqs.length ? Math.max(...seqs) : 0) + 1;
    };

    const onAddRow = () => {
      gridRefs.current["grid1"].current?.addRow({
        no_issue: noIssue,
        seq: nextSeq(),
        yn_active: "Y",
      });
    };

    const onDeleteRow = () => {
      gridRefs.current["grid1"].current?.deleteSelectedRows?.();
    };

    const onClientTargetSelect = async () => {
      if (!noIssue) {
        await showDialog("alter", "이슈 번호가 없습니다.");
        return;
      }

      const result = await showIssueModal({
        title: "DB Sync 업체 선택",
        width: "60%",
        height: "70%",
        children: (
          <SN_SFA_ISSUE_REG_005_POP_006
            noIssue={noIssue}
            selectedClientTargets={selectedClientTargets}
          />
        ),
      });

      if (Array.isArray(result)) {
        setSelectedClientTargets(result);
      }
    };

    const validateRows = async (rows: any[]) => {
      for (const row of rows) {
        if (!trim(row?.sync_type)) {
          await showDialog("alter", "동기화 구분을 입력하세요.");
          return false;
        }
        if (!trim(row?.object_type)) {
          await showDialog("alter", "대상 유형을 입력하세요.");
          return false;
        }
        if (!trim(row?.object_ref)) {
          await showDialog("alter", "대상 값을 입력하세요.");
          return false;
        }
      }
      return true;
    };

    const mapRows = (rows: any[]) =>
      rows.map((row) =>
        normalizeItemRow(row, noIssue, trim(userInfo?.cd_user))
      );

    async function onSave() {
      if (!noIssue) {
        await showDialog("alter", "이슈 번호가 없습니다.");
        return false;
      }

      const grid = gridRefs.current["grid1"].current;
      grid?.stopEditing?.();
      const changes = grid?.getChange?.() ?? {
        modifiedRowcount: 0,
        insert: [],
        update: [],
        delete: [],
      };

      const targetChanged = Array.isArray(selectedClientTargets);
      if (!changes.modifiedRowcount && !targetChanged) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const rowsToValidate = [...(changes.insert ?? []), ...(changes.update ?? [])];
      if (!(await validateRows(rowsToValidate))) return false;

      const saveData: any = {
        no_issue: noIssue,
        cd_user: userInfo?.cd_user,
        sfa_deploy_db_item: {
          insert: mapRows(changes.insert ?? []),
          update: mapRows(changes.update ?? []),
          delete: mapRows(changes.delete ?? []),
        },
      };

      if (targetChanged) {
        saveData.sfa_deploy_db_target = {
          save: selectedClientTargets,
        };
      }

      await fnSave("SN_SFA_ISSUE_REG_005_POP_003_SAVE", saveData);
      return true;
    }

    useImperativeHandle(ref, () => ({
      onSave,
    }));

    const selectedCount =
      selectedClientTargets === undefined
        ? persistedClientTargetCount
        : selectedTargetCount(selectedClientTargets);

    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: {},
        },
      }),
      [columns.grid1, dataSources.grid1]
    );

    return (
      <div className="h-full flex flex-col">
        <div className="flex justify-end items-center mb-2 gap-3">
          <div>
            <SnButton text="추가" onClick={onAddRow} />
            <SnButton text="삭제" onClick={onDeleteRow} />
            <SnButton text="업체 선택" onClick={onClientTargetSelect} />
            <span className="text-sm ml-2">
              업체 {selectedCount ?? 0}개 선택
            </span>
          </div>
        </div>
        <div className="flex-1 min-h-0">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{
              title: "DB Script",
              rowSelection: "multiple",
              popupParent,
              enableInitialSort: false,
            }}
            events={grids.grid1.events}
          />
        </div>
      </div>
    );
  }
);

export default SN_SFA_ISSUE_REG_005_POP_003;
